import { useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Image, X, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GalleryPage = () => {
  const [items, setItems] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios.get(`${API}/gallery`).then(res => setItems(res.data)).catch(() => {});
  }, []);

  const openLightbox = (index) => setLightbox(index);
  const closeLightbox = () => setLightbox(null);
  const prev = () => setLightbox(i => (i > 0 ? i - 1 : items.length - 1));
  const next = () => setLightbox(i => (i < items.length - 1 ? i + 1 : 0));

  useEffect(() => {
    const handleKey = (e) => {
      if (lightbox === null) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  });

  return (
    <div className="min-h-screen" data-testid="gallery-page">
      <Header />
      <main>
        <section className="py-12 md:py-16 bg-gradient-to-b from-[#fff5f5] to-[#FFFBF5]">
          <div className="container-gymtots text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>
              Our <span className="text-[#ff3131]">Gallery</span>
            </h1>
            <p className="text-[#636E72] max-w-xl mx-auto">
              Take a look at our classes in action!
            </p>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-white">
          <div className="container-gymtots">
            {items.length === 0 ? (
              <div className="text-center py-16">
                <Image className="w-16 h-16 text-[#636E72]/30 mx-auto mb-4" />
                <p className="text-[#636E72]">Gallery coming soon!</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {items.map((item, i) => (
                  <div
                    key={item.id}
                    className="relative group cursor-pointer rounded-xl overflow-hidden bg-gray-100 aspect-square"
                    onClick={() => openLightbox(i)}
                    data-testid={`gallery-item-${item.id}`}
                  >
                    {item.media_type === "video" ? (
                      <video
                        src={`${item.url}`}
                        className="w-full h-full object-cover"
                        muted
                        preload="metadata"
                      />
                    ) : (
                      <img
                        src={`${item.url}`}
                        alt={item.caption || "Gallery"}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    )}
                    {item.media_type === "video" && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center">
                          <div className="w-0 h-0 border-t-[8px] border-t-transparent border-l-[14px] border-l-white border-b-[8px] border-b-transparent ml-1" />
                        </div>
                      </div>
                    )}
                    {item.caption && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-white text-xs">{item.caption}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Lightbox */}
      {lightbox !== null && items[lightbox] && (
        <div className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center" onClick={closeLightbox} data-testid="gallery-lightbox">
          <button onClick={closeLightbox} className="absolute top-4 right-4 text-white/70 hover:text-white z-10">
            <X className="w-8 h-8" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 text-white/70 hover:text-white z-10">
            <ChevronLeft className="w-10 h-10" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 text-white/70 hover:text-white z-10">
            <ChevronRight className="w-10 h-10" />
          </button>
          <div className="max-w-5xl max-h-[90vh] w-full mx-4" onClick={(e) => e.stopPropagation()}>
            {items[lightbox].media_type === "video" ? (
              <video
                src={`${items[lightbox].url}`}
                className="max-w-full max-h-[85vh] mx-auto rounded-lg"
                controls
                autoPlay
              />
            ) : (
              <img
                src={`${items[lightbox].url}`}
                alt={items[lightbox].caption || ""}
                className="max-w-full max-h-[85vh] mx-auto rounded-lg object-contain"
              />
            )}
            {items[lightbox].caption && (
              <p className="text-white text-center mt-3 text-sm">{items[lightbox].caption}</p>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default GalleryPage;
